import { ReactNode, useCallback, useState, useRef, useMemo, useEffect } from "react";
import styles from "@/pages/index.module.css";
import { Step } from "@/typings/step";
import { Guess } from "@/typings/guess";
import { generateImageForShare } from "@/utils/image";
import { isSuccess } from "@/utils/scoring";

type DirectionPromptMessageProps = {
  prompt: string;
};

function DirectionPromptMessage({ prompt }: DirectionPromptMessageProps) {
  return <p className={styles.directionsPromptText}>{prompt}</p>;
}

type DirectionResultsSummaryProps = {
  previousGuesses: Array<Guess>;
};

function DirectionResultsSummary({ previousGuesses }: DirectionResultsSummaryProps) {
  return (
    <div className={styles.directionsResult}>
      {previousGuesses.map((previousGuess) =>
        isSuccess(previousGuess.score) ? (
          <div key={previousGuess.guess} className={styles.directionsResultSuccessIcon}>
            {/* Trailing text fixes iOS Safari icon coloring issue by rendering in text style vs emoji style */}
            ✔&#xFE0E;
          </div>
        ) : (
          <div key={previousGuess.guess} className={styles.directionsResultFailIcon}>
            ✗
          </div>
        )
      )}
      {new Array(3 - previousGuesses.length).fill("blank").map((value, index) => (
        <div key={index} className={styles.directionsResultBlankIcon}>
          ·
        </div>
      ))}
    </div>
  );
}

type DirectionShareResultsMessageProps = {
  previousGuesses: Array<Guess>;
};

function DirectionShareResultsMessage({ previousGuesses }: DirectionShareResultsMessageProps) {
  const blob = useMemo(async () => await generateImageForShare(previousGuesses), [previousGuesses]);

  const copyMessageTimeout = useRef<NodeJS.Timeout | null>(null);
  const [isCopied, setIsCopied] = useState(false);
  const onCopySuccess = useCallback(() => {
    setIsCopied(true);
    if (copyMessageTimeout.current) {
      clearTimeout(copyMessageTimeout.current);
    }
    copyMessageTimeout.current = setTimeout(() => {
      setIsCopied(false);
    }, 5000);
  }, []);

  return (
    <div className={styles.directionsShareResultsMessage}>
      <button
        className={`${styles.directionsAction} ${styles.directionsTextLarge}`}
        onClick={async () => {
          await navigator.clipboard.write([
            new ClipboardItem({
              "image/png": blob,
            }),
          ]);

          onCopySuccess();
        }}
      >
        ↝ Share results ↜
      </button>
      {isCopied && <div className={styles.directionsCopySuccessMessage}>Copied!</div>}
    </div>
  );
}

type DirectionMessagesProps = {
  topMessage?: ReactNode;
  centerMessage?: ReactNode;
  bottomMessage?: ReactNode;
  shouldAllowDraw?: boolean;
  shouldFadeIn?: boolean;
};

function DirectionMessages({
  topMessage,
  centerMessage,
  shouldAllowDraw = false,
  shouldFadeIn = false,
}: DirectionMessagesProps) {
  return (
    <div
      className={`
        ${styles.directionsContainer}
        ${shouldAllowDraw ? styles.directionsContainerDrawable : ""}
        ${shouldFadeIn ? styles.directionsContainerFadeIn : ""}
      `}
    >
      {topMessage && <div>{topMessage}</div>}
      {centerMessage && <div className={styles.directionsCenterMessage}>{centerMessage}</div>}
    </div>
  );
}

type DirectionProps = {
  step: Step;
  prompt: string;
  previousGuesses: Array<Guess>;
  onUpdateStep: (step: Step) => void;
};

export function Direction({
  step,
  prompt,
  previousGuesses,
  onUpdateStep,
}: DirectionProps): ReactNode {
  const [isDelayingFinalTransition, setIsDelayingFinalTransition] = useState(false);

  useEffect(() => {
    // We should always delay final transition when the user is playing the game.
    // If the user is just refreshing the page, show them the screen immediately
    if ([Step.Start, Step.RoundOneFail, Step.RoundTwoFail].includes(step)) {
      setIsDelayingFinalTransition(true);
    }

    if ([Step.Success, Step.RoundThreeFail].includes(step)) {
      setTimeout(() => {
        setIsDelayingFinalTransition(false);
      }, 500);
    }
  }, [step]);

  switch (step) {
    case Step.Start:
      return (
        <DirectionMessages
          centerMessage={
            <>
              <p>You have 3 rounds to draw today's phrase.</p>
              <p>Can the pigeon guess it correctly?</p>
              <button
                className={`${styles.directionsAction} ${styles.directionsTextLarge}`}
                onClick={() => {
                  onUpdateStep(Step.RoundOne);
                }}
              >
                ↝ Start ↜
              </button>
            </>
          }
        />
      );

    case Step.RoundOne:
    case Step.RoundTwo:
    case Step.RoundThree:
      return (
        <DirectionMessages
          topMessage={<DirectionPromptMessage prompt={prompt} />}
          shouldAllowDraw={true}
        />
      );

    case Step.RoundOneGuessing:
    case Step.RoundTwoGuessing:
    case Step.RoundThreeGuessing:
    case Step.RoundOneFail:
    case Step.RoundTwoFail:
    case Step.RoundThreeFail:
    case Step.Success:
      if (![Step.Success, Step.RoundThreeFail].includes(step) || isDelayingFinalTransition) {
        return <DirectionMessages topMessage={<DirectionPromptMessage prompt={prompt} />} />;
      } else if (step === Step.RoundThreeFail) {
        return (
          <DirectionMessages
            shouldFadeIn={true}
            centerMessage={
              <>
                <DirectionResultsSummary previousGuesses={previousGuesses} />
                The pigeon wasn't able to guess "{prompt}" from your doodle. Try again tomorrow!
                <DirectionShareResultsMessage previousGuesses={previousGuesses} />
              </>
            }
          />
        );
      } else if (step === Step.Success) {
        return (
          <DirectionMessages
            shouldFadeIn={true}
            centerMessage={
              <>
                <DirectionResultsSummary previousGuesses={previousGuesses} />
                The pigeon successfully guessed "{prompt}" from your doodle. Come back tomorrow for
                a new challenge!
                <DirectionShareResultsMessage previousGuesses={previousGuesses} />
              </>
            }
          />
        );
      }
  }

  return null;
}
